import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import MyCustomTabs from "../../components/common/MyCustomTabs";
import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore";
import AnswerContainer from "../../components/ui/AnswerContainer";
import toast from "react-hot-toast";
import { ROUTES } from "../../constant/routes";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

type FlexibleField = string | string[] | Record<string, any>;

const ROLES = {
  student: String(import.meta.env.VITE_STUDENT_ID).trim(),
  productTeam: String(import.meta.env.VITE_PRODUCT_TEAM_ID).trim(),
};

interface BaseAnswer {
  questionAnswerId: string;
  answer: string;
  isPaid: string;
}

interface ConceptNote {
  concept: FlexibleField;
  "statutory provision": FlexibleField;
  "One example with explanation": FlexibleField;
  summary: FlexibleField;
}

interface ShortNote {
  title: FlexibleField;
  meaning: FlexibleField;
  "statutory provision": FlexibleField;
  "key elements": FlexibleField;
  exceptions: FlexibleField;
  examples: FlexibleField;
  "most recent landmark case":
    | FlexibleField
    | {
        name?: FlexibleField;
        facts?: FlexibleField;
        judgment?: FlexibleField;
        relevance?: FlexibleField;
      };
  "key points": FlexibleField;
  summary: FlexibleField;
}

interface EssayNote {
  title: FlexibleField;
  meaning: FlexibleField;
  "statutory provision": FlexibleField;
  "key elements": FlexibleField;
  exceptions: FlexibleField;
  examples: FlexibleField;
  "most recent landmark case": FlexibleField;
  "key points": FlexibleField;
  summary: FlexibleField;
}

interface CaseStudy {
  title: FlexibleField;
  facts_of_the_case: FlexibleField;
  facts_in_issue: FlexibleField;
  applicable_principle: FlexibleField;
  recent_landmark_case: {
    title: FlexibleField;
    facts: FlexibleField;
    held: FlexibleField;
    relevance: FlexibleField;
  };
  summary: FlexibleField;
}

const deepNormalize = (input: unknown): any => {
  if (Array.isArray(input)) return input.map(deepNormalize);
  if (input && typeof input === "object") {
    const keys = Object.keys(input);
    if (keys.every((k) => /^\d+$/.test(k))) {
      return keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => deepNormalize((input as Record<string, unknown>)[k]))
        .join("")
        .trim();
    }
    return Object.fromEntries(
      keys.map((k) => [k, deepNormalize((input as Record<string, unknown>)[k])])
    );
  }
  return input;
};

const renderContent = (
  data: FlexibleField | null | undefined,
  isPaid: boolean
): React.ReactNode => {
  if (!isPaid) {
    return (
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm py-20 text-center">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Subscribe to view the answers
        </h2>
        <Link
          to={ROUTES.SUBJECT}
          className="bg-myButtonBg text-white px-6 py-2 rounded-md hover:bg-myLabelText transition cursor-pointer"
        >
          Subscribe Now
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="italic text-gray-400 max-w-5xl mx-auto">
        Answers will be available soon.
      </div>
    );
  }

  if (typeof data === "object" && !Array.isArray(data)) {
    return (
      <ul className="list-disc list-inside space-y-1">
        {Object.entries(data).map(([key, value]) => (
          <li key={key}>
            <strong>{key}:</strong>{" "}
            {typeof value === "string" ? value : renderContent(value, isPaid)}
          </li>
        ))}
      </ul>
    );
  }

  if (Array.isArray(data)) {
    return (
      <ul className="list-disc pl-5 space-y-1">
        {data.map((item, idx) => (
          <li key={idx}>
            {typeof item === "string" ? item : renderContent(item, isPaid)}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof data === "string" && data.trim() !== "") {
    return <div className="answerDescription">{data}</div>;
  }

  return (
    <div className="italic text-gray-400 max-w-5xl mx-auto">
      Answers will be available soon.
    </div>
  );
};

const AnswerPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, clearSession, user } = useAuthStore();
  const { questionReference } = useParams<{ questionReference: string }>();

  const [isPaid, setIsPaid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [conceptNotes, setConceptNotes] = useState<ConceptNote | null>(null);
  const [shortNotes, setShortNotes] = useState<ShortNote | null>(null);
  const [essayNotes, setEssayNotes] = useState<EssayNote | null>(null);
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);

  const subjectName = location.state?.subjectName ?? "Unknown Subject";
  const subjectId = location.state?.subjectId ?? "Unknown Subject Id";
  const question = location.state?.question ?? "Unknown Question";

  const fetchAnswer = useCallback(async () => {
    if (!questionReference || !token) {
      setError("Missing question ID or token.");
      setIsLoading(false);
      return;
    }
    const ApiUrl =
      user?.roleId === ROLES.productTeam
        ? `${API_BASE_URL}/allquestionreference`
        : `${API_BASE_URL}/questionanswer`;
    try {
      const response = await axios.get(ApiUrl, {
        params: {
          questionReference,
          subjectId,
          referenceType: "CHATGPT",
          userId: user?.userId,
        },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const answers: BaseAnswer[] = response.data.success || [];
      if (answers.length === 0) {
        setError("No answers found.");
        return;
      }
      // added connditional check for product team role
      const isProductTeam = user?.roleId === ROLES.productTeam;
      let paidStatus = true;
      if (!isProductTeam) {
        paidStatus = answers[0].isPaid === "true";
      }
      setIsPaid(paidStatus);
      if (!paidStatus && !isProductTeam) {
        answers.forEach(({ questionAnswerId, answer }) => {
          if (questionAnswerId.endsWith("_ACP")) {
            try {
              const parsed = deepNormalize(JSON.parse(answer));
              setConceptNotes(parsed);
            } catch {
              toast.error("Invalid or unexpected answer format.");
            }
          }
        });
        return;
      }
      // const paidStatus = answers[0].isPaid === "true";
      // setIsPaid(paidStatus);
      // if (!paidStatus) {
      //   // Continue to show concept only
      //   answers.forEach(({ questionAnswerId, answer }) => {
      //     if (questionAnswerId.endsWith("_ACP")) {
      //       try {
      //         const parsed = deepNormalize(JSON.parse(answer));
      //         setConceptNotes(parsed);
      //         // eslint-disable-next-line @typescript-eslint/no-unused-vars
      //       } catch (err) {
      //         toast.error("Invalid or unexpected answer format.");
      //       }
      //     }
      //   });
      //   return;
      // }

      answers.forEach(({ questionAnswerId, answer }) => {
        try {
          const parsed = deepNormalize(JSON.parse(answer));
          if (questionAnswerId.endsWith("_ACP")) setConceptNotes(parsed);
          else if (questionAnswerId.endsWith("_ASH")) setShortNotes(parsed);
          else if (questionAnswerId.endsWith("_AEA")) setEssayNotes(parsed);
          else if (questionAnswerId.endsWith("_ACS")) setCaseStudy(parsed);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          toast.error("Invalid or unexpected answer format.");
        }
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorMessage =
          err.response?.data?.message || "Failed to fetch answers.";
        setError(errorMessage);
        clearSession("expired");
      } else {
        setError("Unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [questionReference, token, clearSession, subjectId, user?.userId]);

  useEffect(() => {
    const disableActions = (e: Event) => e.preventDefault();
    const disableKeys = (e: KeyboardEvent) => {
      const blocked =
        e.key === "F12" ||
        (e.ctrlKey && ["u", "U", "a", "c", "v", "x"].includes(e.key)) ||
        (e.ctrlKey &&
          e.shiftKey &&
          ["I", "i", "J", "j", "C", "c"].includes(e.key));
      if (blocked) e.preventDefault();
    };

    document.addEventListener("contextmenu", disableActions);
    document.addEventListener("keydown", disableKeys);
    fetchAnswer();

    return () => {
      document.removeEventListener("contextmenu", disableActions);
      document.removeEventListener("keydown", disableKeys);
    };
  }, [fetchAnswer]);

  // ✅ Allow Concept tab even when not paid
  const renderSection = (
    answer: Record<string, any> | null,
    fields: [string, FlexibleField | undefined][],
    forceShow: boolean = false
  ) => {
    const showAnswer = forceShow ? true : isPaid;
    if (!answer) return renderContent(null, showAnswer);
    return (
      <div className="space-y-4 max-w-5xl mx-auto">
        {fields.map(([title, value]) => (
          <AnswerContainer
            key={title}
            title={title}
            data={renderContent(value, showAnswer)}
          />
        ))}
      </div>
    );
  };

  const tabs = [
    {
      label: "Concept",
      content: renderSection(
        conceptNotes,
        [
          ["Concept", conceptNotes?.concept],
          ["Statutory Provision", conceptNotes?.["statutory provision"]],
          [
            "One example with explanation",
            conceptNotes?.["One example with explanation"],
          ],
          ["Summary", conceptNotes?.summary],
        ],
        true // ✅ Always show Concept tab answers
      ),
    },
    {
      label: "Short Notes",
      content: renderSection(shortNotes, [
        ["Title", shortNotes?.title],
        ["Meaning", shortNotes?.meaning],
        ["Statutory Provision", shortNotes?.["statutory provision"]],
        ["Key Elements", shortNotes?.["key elements"]],
        ["Exceptions", shortNotes?.exceptions],
        ["Examples", shortNotes?.examples],
        ["Landmark Case", shortNotes?.["most recent landmark case"]],
        ["Key Points", shortNotes?.["key points"]],
        ["Summary", shortNotes?.summary],
      ]),
    },
    {
      label: "Essay Notes",
      content: renderSection(essayNotes, [
        ["Title", essayNotes?.title],
        ["Meaning", essayNotes?.meaning],
        ["Statutory Provision", essayNotes?.["statutory provision"]],
        ["Key Elements", essayNotes?.["key elements"]],
        ["Exceptions", essayNotes?.exceptions],
        ["Examples", essayNotes?.examples],
        ["Landmark Case", essayNotes?.["most recent landmark case"]],
        ["Key Points", essayNotes?.["key points"]],
        ["Summary", essayNotes?.summary],
      ]),
    },
    {
      label: "Case Study",
      content: renderSection(caseStudy, [
        ["Title", caseStudy?.title],
        ["Facts of the Case", caseStudy?.facts_of_the_case],
        ["Facts in Issue", caseStudy?.facts_in_issue],
        ["Applicable Principle", caseStudy?.applicable_principle],
        ["Landmark Case", caseStudy?.recent_landmark_case],
        ["Summary", caseStudy?.summary],
      ]),
    },
  ];

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-myButtonBg hover:bg-myLabelText text-white px-6 py-2 rounded-md transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <section
      className="bg-myLight text-myLabelText rounded-2xl h-full overflow-y-auto w-full pb-4"
      style={{ userSelect: "none" }}
    >
      <div className="min-h-full bg-myLight">
        <header className="border-b border-myInputBorder/60 bg-white backdrop-blur-md w-full sticky z-30 top-0 p-4">
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <div className="flex flex-col items-start font-medium sm:text-base text-xs">
              <h1>
                Subject:{" "}
                <span className="sm:font-semibold font-medium">
                  {subjectName}
                </span>
              </h1>
              <h2>
                Question:{" "}
                <span className="sm:font-semibold font-medium">{question}</span>
              </h2>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="cursor-pointer hover:bg-myButtonBg hover:text-myButtonText border border-myButtonBg text-myButtonBg font-semibold sm:py-2.5 py-1.5 sm:px-5 px-2.5 capitalize rounded-md transition-all duration-200 flex items-center md:gap-3 sm:gap-2 gap-1 sm:text-base text-xs"
            >
              <FiArrowLeft className="sm:w-5 sm:h-5 w-4 h-4" /> Back
            </button>
          </div>
        </header>

        <section>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500" />
            </div>
          ) : (
            <MyCustomTabs tabs={tabs} />
          )}
        </section>
      </div>
    </section>
  );
};

export default AnswerPage;
